import {Injectable, Injector} from '@angular/core';
import * as _ from 'lodash-es';
import {Dimension, otherDimension, Point} from './abstract-units';
import {Cell, getNewCellId} from './cell';
import {BOX_SIDES_MAP, BoxEdge, BoxEdges, Edge, EdgeSides, getNewEdgeId, Side} from './edge';
import {Layout} from './layout';
import {LayoutFactoryBase} from './layout-factory-base';
import {CellDefinition, EdgeDefinition, EdgeRefSides, LayoutDefinition} from './layout-definition';
import {Widget} from './widget/widget';
import {WidgetDefinition} from './widget/widget-definition';
import {WidgetFactory} from './widget/widget.factory';

@Injectable()
export class LayoutFactory extends LayoutFactoryBase {

    public static cloneLayoutDefinition(layoutDef: LayoutDefinition, tabId: string = null): LayoutDefinition {
        const newLayoutDef = _.cloneDeep(layoutDef);

        newLayoutDef.tabId = tabId;

        // Generate new CellId
        const newCellsIdDictById: {[oldCellId: string]: string} = {};
        newLayoutDef.cells.forEach(cell => newCellsIdDictById[cell.id] = getNewCellId());

        // Generate new WidgetIds
        const newWidgetIdDictById: {[oldWidgetId: string]: string} = {};
        newLayoutDef.widgets.forEach(widget => newWidgetIdDictById[widget.id] = Widget.getNewWidgetId());

        // Set new CellIds to sides
        newLayoutDef.edges.forEach(edge => edge.sides = edge.sides.map(
            side => side.map(oldCellId => newCellsIdDictById[oldCellId])
        ) as EdgeRefSides);

        // Set new CellIds, widgetIds to cells
        newLayoutDef.cells.forEach(cell => {
            cell.id = newCellsIdDictById[cell.id];
            cell.widgets = cell.widgets.map(widgetId => newWidgetIdDictById[widgetId]);
        });

        // Set new widgetIds, cellId and tabId to widgets
        newLayoutDef.widgets.forEach(widget => {
            widget.tabId = tabId;
            widget.id = newWidgetIdDictById[widget.id];
            widget.cellId = widget.cellId && newCellsIdDictById[widget.cellId]; // FastTrade does not have CellId
        });

        return newLayoutDef;
    }

    // Moved here from constructor as a workaround for strange bug with DI and imports.
    private widgetFactory: WidgetFactory = this.injector.get(WidgetFactory);

    constructor(
        private injector: Injector
    ) {
        super();
    }

    public createEmptyLayout(tabId: string): Layout {
        return this.createLayoutByDefinition({
            size: [100, 100],
            tabId,
            cells: [],
            edges: [],
            widgets: []
        } as LayoutDefinition);
    }

    public createLayoutByDefinition(layoutDef: LayoutDefinition): Layout {
        const size: Point = layoutDef.size;

        // Cells
        const cells: Cell[] = layoutDef.cells.map(cellDef => this.createCellByDefinition(cellDef));
        const cellsDictById = _.keyBy(cells, cell => cell.id);

        // Edges
        const edges: Edge[] = this.createEdgesByDefinition(layoutDef.edges, cellsDictById);
        Layout.bindEdgesToCells(edges);
        this.produceBoundaryEdges(cells, edges, size);

        // Widgets
        const tabId = layoutDef.tabId;
        // set tabId to child WidgetDefinitions
        layoutDef.widgets.map(widget => widget.tabId = tabId);
        const widgets: Widget[] = this.createWidgetsByDefinition(layoutDef.widgets, cellsDictById);

        this.orderCellsWidgetsByDef(cells, layoutDef);

        this.setCellsActiveWidgetsByDef(cells, layoutDef.cells);

        // Layout
        const layout = new Layout(size, tabId, cells, edges, widgets, this.injector);

        layout.sortEdgesCells();

        return layout;
    }

    public getLayoutDefinition(layout: Layout): LayoutDefinition {
        return {
            size:    layout.size.slice() as Point,
            tabId:   layout.tabId,
            cells:   layout.cells.map(cell => this.getCellDefinition(cell)),
            edges:   layout.edges
                         .filter(edge => !edge.isBoundary)
                         .map(edge => this.getEdgeDefinition(edge)),
            widgets: layout.widgets.map(widget => this.widgetFactory.getWidgetDefinition(widget))
        };
    }

    public createCellByDefinition(cellDef: CellDefinition): Cell {
        return {
            id:           cellDef.id || getNewCellId(),
            edges:        [null, null, null, null],
            widgets:      [],
            activeWidget: null,
            display:      null
        };
    }

    private createEdgesByDefinition(edgesDef: EdgeDefinition[], cellsDictById: {[cellId: string]: Cell}): Edge[] {
        return edgesDef.map(edgeDef => {
            const axis = edgeDef.axis;
            const edge: Edge = {
                id:            getNewEdgeId(),
                axis,
                axisRange:     edgeDef.axisRange.slice() as Point,
                movingAxis:    otherDimension(edgeDef.axis),
                movingAxisPos: edgeDef.movingAxisPos,
                sides:         null, // To be populated below.
                isBoundary:    false,
                display:       null
            };

            edge.sides = edgeDef.sides.map(
                cellIds => cellIds.map(cellId => cellsDictById[cellId])
            ) as EdgeSides;

            return edge;
        });
    }

    private createWidgetsByDefinition(
        widgetsDef: WidgetDefinition[],
        cellsDictById: {[cellId: string]: Cell}
    ): Widget[] {
        return widgetsDef.map(
            widgetDef => {
                const cell: Cell = cellsDictById[widgetDef.cellId];

                return this.widgetFactory.createWidgetByDefinition(widgetDef, cell);
            }
        );
    }

    private produceBoundaryEdges(cells: Cell[], edges: Edge[], size: Point): void {
        const [width, height] = size;

        const boundaryEdges: BoxEdges = [
            { // BoxEdge.TOP
                id:            'TOP_LAYOUT_EDGE',
                axis:          Dimension.X,
                axisRange:     [0, width],
                movingAxis:    Dimension.Y,
                movingAxisPos: 0,
                sides:         [[], []],
                isBoundary:    true,
                display:       null
            },
            { // BoxEdge.RIGHT
                id:            'RIGHT_LAYOUT_EDGE',
                axis:          Dimension.Y,
                axisRange:     [0, height],
                movingAxis:    Dimension.X,
                movingAxisPos: width,
                sides:         [[], []],
                isBoundary:    true,
                display:       null
            },
            { // BoxEdge.BOTTOM
                id:            'BOTTOM_LAYOUT_EDGE',
                axis:          Dimension.X,
                axisRange:     [0, width],
                movingAxis:    Dimension.Y,
                movingAxisPos: height,
                sides:         [[], []],
                isBoundary:    true,
                display:       null
            },
            { // BoxEdge.LEFT
                id:            'LEFT_LAYOUT_EDGE',
                axis:          Dimension.Y,
                axisRange:     [0, height],
                movingAxis:    Dimension.X,
                movingAxisPos: 0,
                sides:         [[], []],
                isBoundary:    true,
                display:       null
            }
        ];

        // Append boundary edges to edge list.
        edges.push(...boundaryEdges);

        // Bind boundary edges with cells.
        cells.forEach(cell => {
            cell.edges.forEach((edge, boxEdge: BoxEdge) => {
                if (!edge) {
                    edge = boundaryEdges[boxEdge];
                    cell.edges[boxEdge] = edge;

                    const side: Side = BOX_SIDES_MAP[boxEdge];

                    edge.sides[side].push(cell);
                }
            });
        });
    }

    private getCellDefinition(cell: Cell): CellDefinition {
        return {
            id: cell.id,
            activeWidgetIndex: cell.widgets.indexOf(cell.activeWidget),
            widgets: cell.widgets && cell.widgets.map(widget => widget.id)
        };
    }

    private getEdgeDefinition(edge: Edge): EdgeDefinition {
        return {
            axis: edge.axis,
            movingAxisPos: edge.movingAxisPos,
            axisRange: edge.axisRange.slice() as Point,
            sides: edge.sides
                       .map(sideCells => sideCells
                           .map(cell => cell.id)
                       ) as EdgeRefSides
        };
    }

}
